import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPaperPlane, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';

interface Conversation {
    patient: { patient_id: string; name: string };
    last_message: string;
    last_message_at: string | null;
    unread_count: number;
}

interface PatientOption {
    patient_id: string;
    first_name: string;
    last_name: string;
}

interface Message {
    id: number;
    sender: 'provider' | 'patient';
    content: string;
    read: boolean;
    created: string;
}

const ProviderMessages: React.FC = () => {
    const { api } = useProviderApi();

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [patients, setPatients] = useState<PatientOption[]>([]);
    const [activePatient, setActivePatient] = useState<string>('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [draft, setDraft] = useState('');
    const [loadingConvos, setLoadingConvos] = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => { document.title = 'Communications - Uzima Healthcare'; }, []);

    const loadConversations = async () => {
        setLoadingConvos(true);
        try {
            const res = await api.get('practitioners/conversations/');
            if (res.data?.status === 'success') setConversations(res.data.data || []);
        } catch { /* leave list as-is on failure */ } finally {
            setLoadingConvos(false);
        }
    };

    const loadPatients = async () => {
        try {
            const res = await api.get('practitioners/patients/?status=active');
            if (res.data?.status === 'success') setPatients(res.data.data.map((p: any) => ({ patient_id: p.patient_id, first_name: p.first_name, last_name: p.last_name })));
        } catch { /* patient select stays empty on failure */ }
    };

    useEffect(() => { loadConversations(); loadPatients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadThread = async (patientId: string) => {
        setActivePatient(patientId);
        setLoadingThread(true);
        try {
            const res = await api.get(`practitioners/conversations/${patientId}/`);
            if (res.data?.status === 'success') setMessages(res.data.data || []);
            loadConversations();
        } catch {
            setMessages([]);
        } finally {
            setLoadingThread(false);
        }
    };

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const sendMessage = async () => {
        if (!draft.trim() || !activePatient) return;
        try {
            const res = await api.post(`practitioners/conversations/${activePatient}/`, { content: draft.trim() });
            if (res.data?.status === 'success') {
                setMessages(prev => [...prev, res.data.data]);
                setDraft('');
                loadConversations();
            }
        } catch { /* leave draft in place so the user can retry */ }
    };

    const startNewConversation = (patientId: string) => {
        if (!patientId) return;
        loadThread(patientId);
    };

    const activePatientName = conversations.find(c => c.patient.patient_id === activePatient)?.patient.name
        || patients.find(p => p.patient_id === activePatient)?.first_name;

    return (
        <ProviderShell>
            <div className="mb-4">
                <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faComments} className="me-2 text-primary" />Communications</h2>
                <p className="text-muted mb-0">Message your patients directly</p>
            </div>

            <div className="card" style={{ height: '65vh' }}>
                <div className="row g-0 h-100">
                    <div className="col-4 border-end h-100 d-flex flex-column">
                        <div className="p-3 border-bottom">
                            <select className="form-select form-select-sm" value="" onChange={(e) => startNewConversation(e.target.value)}>
                                <option value="">+ New conversation...</option>
                                {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}
                            </select>
                        </div>
                        <div className="flex-grow-1 overflow-auto">
                            {loadingConvos ? (
                                <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                            ) : conversations.length === 0 ? (
                                <div className="text-center text-muted p-4 small">No conversations yet. Start one above.</div>
                            ) : (
                                conversations.map(c => (
                                    <div key={c.patient.patient_id}
                                         className={`p-3 border-bottom ${activePatient === c.patient.patient_id ? 'bg-light' : ''}`}
                                         style={{ cursor: 'pointer' }}
                                         onClick={() => loadThread(c.patient.patient_id)}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <strong className="small">{c.patient.name}</strong>
                                            {c.unread_count > 0 && <span className="badge bg-danger rounded-pill">{c.unread_count}</span>}
                                        </div>
                                        <div className="text-muted small text-truncate">{c.last_message}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="col-8 h-100 d-flex flex-column">
                        {!activePatient ? (
                            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                <div className="text-center">
                                    <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '2.5rem' }} className="mb-2" />
                                    <p>Select a conversation to start messaging</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="p-3 border-bottom"><strong>{activePatientName}</strong></div>
                                <div className="flex-grow-1 overflow-auto p-3">
                                    {loadingThread ? (
                                        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                                    ) : messages.length === 0 ? (
                                        <div className="text-center text-muted small">No messages yet. Say hello!</div>
                                    ) : (
                                        messages.map(m => (
                                            <div key={m.id} className={`d-flex mb-2 ${m.sender === 'provider' ? 'justify-content-end' : 'justify-content-start'}`}>
                                                <div className={`p-2 px-3 rounded-3 ${m.sender === 'provider' ? 'bg-primary text-white' : 'bg-light'}`} style={{ maxWidth: '70%' }}>
                                                    <div className="small">{m.content}</div>
                                                    <div className={`text-end small mt-1 ${m.sender === 'provider' ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.65rem' }}>
                                                        {new Date(m.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    <div ref={bottomRef} />
                                </div>
                                <div className="p-3 border-top d-flex gap-2">
                                    <input type="text" className="form-control" placeholder="Type a message..."
                                           value={draft} onChange={(e) => setDraft(e.target.value)}
                                           onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }} />
                                    <button className="btn btn-primary" onClick={sendMessage} disabled={!draft.trim()}>
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </ProviderShell>
    );
};

export default ProviderMessages;
